// Fundamentals Dimension — server-persisted admin flag client (Part 1).
//
// READ: any authenticated user reads app_feature_flags['fundamentals_dimension']
// (RLS allows authenticated SELECT; the value is a boolean, not a secret) and feeds
// it into the pure gate's cached state. FAIL-CLOSED: any read error → enabled:false.
//
// WRITE: admins ONLY, and NEVER a direct client DB write — it routes through the
// is_admin-gated `admin-ops` edge function. An unauthorized user calling this gets
// a 401/403 from the function; the browser has no write path to the table (RLS
// revokes client INSERT/UPDATE/DELETE). This is how "unauthorized cannot change it"
// is enforced. The env kill switch (VITE_FUNDAMENTALS_DIMENSION=off) stays supreme.

import { supabase } from './supabaseClient';
import { callEdgeFunction } from '../utils/supabaseFunctions';
import { setFundamentalsAdminFlag } from '../config/fundamentalsDimensionFlag';

const FLAG_KEY = 'fundamentals_dimension';
let started = false;

/** Read the persisted flag and update the gate's cached admin state. Fail-closed. */
export async function loadFundamentalsFlag(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('app_feature_flags')
      .select('enabled')
      .eq('key', FLAG_KEY)
      .maybeSingle();
    const enabled = !error && data?.enabled === true;
    setFundamentalsAdminFlag({ loaded: true, enabled });
    return enabled;
  } catch {
    setFundamentalsAdminFlag({ loaded: true, enabled: false }); // fail-closed
    return false;
  }
}

/** Load once at app start (idempotent). Before the read resolves the gate treats the
 *  admin flag as not-loaded → it does not enable (fail-closed). */
export function ensureFundamentalsFlag(): void {
  if (started) return;
  started = true;
  void loadFundamentalsFlag();
}

/**
 * ADMIN ONLY: enable/disable the fundamentals dimension for everyone (subject to the
 * supreme env kill switch). Routes through admin-ops (is_admin enforced server-side);
 * throws on non-admin / HTTP error. Reloads the cached state on success.
 */
export async function setFundamentalsDimensionEnabled(enabled: boolean): Promise<void> {
  const res = await callEdgeFunction('admin-ops', {
    action: 'set_feature_flag',
    payload: { key: FLAG_KEY, enabled: enabled === true },
  });
  if (!res.ok) {
    let msg = `set_feature_flag HTTP ${res.status}`;
    try { const b = await res.json() as { message?: string; error?: string }; msg = b.message ?? b.error ?? msg; } catch { /* keep */ }
    throw new Error(msg);
  }
  await loadFundamentalsFlag();
}
